import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { TenantService } from "@/services/tenantService";
import type { CreateTenantData } from "@/types/tenant";
import toast from "react-hot-toast";
import { TbCheck, TbX, TbLoader } from "react-icons/tb";

export const PlatformTenantCreate: React.FC = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [createForm, setCreateForm] = useState<CreateTenantData>({
    name: "",
    slug: "",
  });
  const [slugStatus, setSlugStatus] = useState<{
    checking: boolean;
    available: boolean | null;
    reason?: string;
  }>({ checking: false, available: null });
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  // Debounced slug availability check
  useEffect(() => {
    const slug = createForm.slug;

    if (!slug || slug.length < 3) {
      setSlugStatus({ checking: false, available: null });
      return;
    }

    setSlugStatus({ checking: true, available: null });

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const result = await TenantService.checkSlugAvailability(slug);
      setSlugStatus({
        checking: false,
        available: result.available,
        reason: result.reason,
      });
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [createForm.slug]);

  const handleCreateTenant = async () => {
    if (!createForm.name.trim() || !createForm.slug.trim()) {
      toast.error("Name and slug are required");
      return;
    }

    if (slugStatus.available === false) {
      toast.error(slugStatus.reason || "Slug is not available");
      return;
    }

    setSaving(true);
    try {
      await TenantService.createTenant(createForm);
      toast.success("Tenant created successfully");
      navigate("/platform/tenants");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to create tenant"
      );
    } finally {
      setSaving(false);
    }
  };

  const slugBorderColor =
    slugStatus.available === true
      ? "border-green-400 focus:ring-green-500"
      : slugStatus.available === false
        ? "border-red-400 focus:ring-red-500"
        : "border-gray-300 focus:ring-[#374795]";

  return (
    <div className="w-full space-y-6">
      <h2 className="text-lg font-semibold text-[#444]">Create New Tenant</h2>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-[#444] mb-1.5">
            Tenant Name
          </label>
          <input
            type="text"
            value={createForm.name}
            onChange={(e) => {
              const name = e.target.value;
              setCreateForm((prev) => ({
                ...prev,
                name,
                slug:
                  prev.slug ||
                  name
                    .toLowerCase()
                    .replace(/[^a-z0-9]/g, "-")
                    .replace(/-+/g, "-"),
              }));
            }}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
            placeholder="Acme Corporation"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#444] mb-1.5">
            Subdomain
          </label>
          <div className="flex items-center">
            <div className="relative flex-1">
              <input
                type="text"
                value={createForm.slug}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    slug: e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9-]/g, ""),
                  }))
                }
                className={`w-full px-4 py-2.5 pr-10 border rounded-l-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent ${slugBorderColor}`}
                placeholder="acme"
              />
              {createForm.slug.length >= 3 && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {slugStatus.checking ? (
                    <TbLoader className="w-4 h-4 animate-spin text-gray-400" />
                  ) : slugStatus.available === true ? (
                    <TbCheck className="w-4 h-4 text-green-500" />
                  ) : slugStatus.available === false ? (
                    <TbX className="w-4 h-4 text-red-500" />
                  ) : null}
                </div>
              )}
            </div>
            <span className="px-4 py-2.5 bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg text-sm text-[#828282]">
              .taskrsol.com
            </span>
          </div>
          {/* Status message */}
          {createForm.slug.length >= 3 && !slugStatus.checking && (
            <p
              className={`mt-1.5 text-xs ${
                slugStatus.available === true
                  ? "text-green-600"
                  : slugStatus.available === false
                    ? "text-red-500"
                    : "text-[#828282]"
              }`}
            >
              {slugStatus.available === true
                ? `${createForm.slug}.taskrsol.com is available`
                : slugStatus.available === false
                  ? slugStatus.reason || "Not available"
                  : ""}
            </p>
          )}
          {createForm.slug.length > 0 && createForm.slug.length < 3 && (
            <p className="mt-1.5 text-xs text-[#828282]">
              Must be at least 3 characters
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            onClick={() => navigate("/platform/tenants")}
            className="px-5 py-2.5 text-sm font-medium text-[#444] bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateTenant}
            disabled={
              saving || slugStatus.available === false || slugStatus.checking
            }
            className="px-5 py-2.5 text-sm font-medium text-white bg-[#374795] rounded-lg hover:bg-[#1c398e] disabled:opacity-50 transition-colors cursor-pointer"
          >
            {saving ? "Creating..." : "Create Tenant"}
          </button>
        </div>
      </div>
    </div>
  );
};
